import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Detects whether the pills inside the container overflow the available width.
 *
 * Key design: hidden pills use display:none (via the pillHidden CSS class) so they
 * don't take up space in the collapsed layout. To measure the true natural width of
 * all pills, we temporarily override display on each pill with an inline style during
 * measurement, then restore it. This is intentional DOM measurement — the inline style
 * is never left in place after measureNaturalWidth returns.
 *
 * The two observers both re-measure and re-check on every change:
 * - MutationObserver: fires when pill content changes (e.g. new follow-ups arrive)
 * - ResizeObserver: fires when the container is resized; re-measurement is required
 *   (not just re-checking) because hidden pills are excluded from scrollWidth, so
 *   naturalWidthRef must be refreshed against the current DOM state on every resize
 */
export const useOverflowDetection = (): {
	containerRef: React.RefObject<HTMLDivElement>;
	isOverflowing: boolean;
} => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [isOverflowing, setIsOverflowing] = useState(false);
	// Stores the total natural (uncollapsed) width of all pills + gaps
	const naturalWidthRef = useRef<number>(0);

	const measureNaturalWidth = useCallback(() => {
		const el = containerRef.current;
		if (!el) {
			return;
		}
		const pills = Array.from(el.querySelectorAll('[data-pill]')) as HTMLElement[];
		if (pills.length === 0) {
			naturalWidthRef.current = 0;
			return;
		}
		// Temporarily override display on all pills so scrollWidth reflects the full
		// natural width. We use 'inline-flex' so pills maintain their row layout
		// during measurement (using 'block' would stack them vertically, giving an
		// incorrect scrollWidth). After reading scrollWidth, we restore the original
		// inline style value ('' for pills that had no prior inline style, which lets
		// the CSS class re-assert display:none correctly).
		const prevDisplays = pills.map((p) => p.style.display);
		pills.forEach((p) => {
			p.style.display = 'inline-flex';
		});
		naturalWidthRef.current = el.scrollWidth;
		pills.forEach((p, i) => {
			p.style.display = prevDisplays[i];
		});
	}, []);

	const checkOverflow = useCallback(() => {
		const el = containerRef.current;
		if (!el) {
			return;
		}
		setIsOverflowing(naturalWidthRef.current > el.clientWidth);
	}, []);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) {
			return;
		}

		// MutationObserver re-measures natural widths when pill content changes
		const mutationObserver = new MutationObserver(() => {
			measureNaturalWidth();
			checkOverflow();
		});
		mutationObserver.observe(el, { childList: true, subtree: true });

		// ResizeObserver re-measures AND re-checks on every container resize.
		// Re-measurement is required (not just re-checking) because the hidden pills
		// are excluded from scrollWidth — so naturalWidthRef must be refreshed against
		// the current DOM state each time the container changes size.
		const resizeObserver = new ResizeObserver(() => {
			measureNaturalWidth();
			checkOverflow();
		});
		resizeObserver.observe(el);

		// Defer initial measurement until after layout has settled.
		// Running synchronously risks measuring against a temporarily narrow
		// clientWidth (e.g. while a dialog is animating open), which would
		// produce a false-positive overflow that never self-corrects.
		const rafId = requestAnimationFrame(() => {
			measureNaturalWidth();
			checkOverflow();
		});

		return () => {
			cancelAnimationFrame(rafId);
			mutationObserver.disconnect();
			resizeObserver.disconnect();
		};
	}, [measureNaturalWidth, checkOverflow]);

	return { containerRef, isOverflowing };
};
